import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Backdrop, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Button } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ToastMessage from "../utils/mui/righttoptoast";
import { defaultDepartmentId, hasMultipleDepartments, practiceDepartmentName, practiceDepartments } from "../config/practice";
import { eventBus } from "../utils/eventBus";
import { inboxErrorMessage, itemCandidates, linkItem, listRequests, sendIntakeLink, updateItem } from "./inboxApi";

/*
 * Inbox > Requests (owner 10-20, Step 1): website appointment requests,
 * website new-patient requests and onboarding intakes as ONE list, newest
 * first, each with a type chip, a status (New / In progress / Waiting on
 * patient / Done), an assignee and a note (v2 /v1/{pid}/inbox/requests and
 * PUT inbox/item; the old tabs' own status columns are kept in sync by the
 * server). A website request whose phone + last name match a chart shows a
 * chip; "Link to chart" posts it into that patient's chat thread. "Send
 * intake link" is the Web Requests tab's action. An onboarding intake opens
 * in the Onboarding view (`onOpenView("onboarding")`), whose viewer and
 * Submit to EMR are unchanged.
 */
export const STATUS_META = {
  new: { label: "New", cls: "text-[#1E40AF] bg-[#DBEAFE] border border-[#93C5FD]" },
  in_progress: { label: "In progress", cls: "text-[#6B21A8] bg-[#F3E8FF] border border-[#D8B4FE]" },
  waiting_patient: { label: "Waiting on patient", cls: "text-[#B45309] bg-[#FFFBEB] border border-[#FCD34D]" },
  done: { label: "Done", cls: "text-[#166534] bg-[#F0FDF4] border border-[#86EFAC]" },
  expired: { label: "Expired", cls: "text-[#475467] bg-[#F2F4F7] border border-[#D0D5DD]" },
};
export const STATUS_ORDER = ["new", "in_progress", "waiting_patient", "done"];

// Onboarding's waiting_patient is the old tab's "Needs Call Back": the
// patient's intake link no longer accepts edits there, so it must not read
// "Waiting on patient" (review 10-20; the server sends the same status_label).
export function statusLabel(source, status) {
  if (source === "onboarding" && status === "waiting_patient") return "Needs call back";
  return (STATUS_META[status] || STATUS_META.new).label;
}

export const FILTER_CHIPS = [
  { value: "open", label: "Open" },
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "waiting_patient", label: "Waiting on patient" },
  { value: "done", label: "Done" },
  { value: "expired", label: "Expired" },
  { value: "all", label: "All" },
];

export const TYPE_CHIPS = {
  web_appointment: { label: "Appointment", cls: "bg-[#EEF4FF] text-[#3538CD] border-[#C7D7FE]" },
  web_new_patient: { label: "New patient (website)", cls: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  // staff create the chart first, then text the intake paperwork link (owner 10-20)
  onboarding: { label: "New-patient paperwork", cls: "bg-[#FFF6ED] text-[#C4320A] border-[#FDDCAB]" },
  web_inquiry: { label: "Website inquiry", cls: "bg-[#F4F3FF] text-[#5925DC] border-[#D9D6FE]" },
};

const PAGE_SIZE = 50;
const itemKey = (i) => `${i.source}:${i.source_id}`;
const isWeb = (i) => i.source === "web_appointment" || i.source === "web_new_patient";

// The one short chip a row carries (single line), or null.
export function matchChip(item) {
  if (item.auto_closed && item.auto_closed.reason === "registered") return { label: "Chart created", cls: "bg-[#ECFDF3] text-[#027A48]" };
  if (item.routed && item.routed.routed_to) {
    // a new-patient request that was routed stays open for the Requests person to confirm
    if (item.source === "web_new_patient") return { label: "Says new - has chart", cls: "bg-[#FFFAEB] text-[#B54708]" };
    return { label: "In patient chat", cls: "bg-[#ECFDF3] text-[#027A48]" };
  }
  const m = item.match;
  if (!m) return null;
  if (m.state === "matched") {
    // the exact chart shows as its own "Chart #id" pill (chartPill)
    return item.source === "web_new_patient" ? { label: "Says new - has chart", cls: "bg-[#FFFAEB] text-[#B54708]" } : null;
  }
  if (m.state === "possible") return { label: `Maybe has chart (${m.count})`, cls: "bg-[#FFFAEB] text-[#B54708]" };
  return null;
}

// The chart a request belongs to, as its own pill next to the name (owner 10-20):
//   {patientid} -> "Chart #id" (opens the chart), {none: true} -> amber "No chart yet"
//   (paperwork without a chart), null -> no pill. Website requests: the chart they were
//   sent to, the chart created after them, or their exact match.
export function chartPill(item) {
  const d = item.details || {};
  if (item.source === "onboarding") {
    const id = String(d.patientid || "").trim();
    return /^\d+$/.test(id) && Number(id) > 0 ? { patientid: Number(id) } : { none: true };
  }
  if (item.source !== "web_appointment" && item.source !== "web_new_patient") return null;
  if (item.routed && item.routed.patientid) return { patientid: item.routed.patientid };
  if (item.auto_closed && item.auto_closed.reason === "registered" && item.auto_closed.patientid) {
    return { patientid: item.auto_closed.patientid };
  }
  if (item.match && item.match.state === "matched" && item.match.patientid) return { patientid: item.match.patientid };
  return null;
}

// The routing / closing result as one readable line, or null.
export function resultLine(item) {
  const r = item.routed;
  if (r && r.routed_to) {
    const who = r.patient_name ? `${r.patient_name} (#${r.patientid})` : `#${r.patientid}`;
    return `Sent to chat of ${who}`;
  }
  const c = item.auto_closed;
  // when care first saw the chart (patients.ts), not the Athena registration time
  if (c && c.reason === "registered") return `Chart created (#${c.patientid})`;
  if (c && c.reason === "expired") return `Expired ${c.at ? dayjs(c.at).format("MM-DD-YYYY") : ""}`.trim();
  return null;
}

// "Link to chart" shows as the row's main action while a match is waiting.
export function canLink(item, meta) {
  return (
    (item.source === "web_appointment" || item.source === "web_new_patient") &&
    meta.routingReady &&
    !item.routed &&
    !(item.auto_closed && item.auto_closed.reason === "registered") &&
    item.match &&
    (item.match.state === "matched" || item.match.state === "possible")
  );
}

function requestedLine(item) {
  const d = item.details || {};
  if (item.source === "onboarding") return d.dob ? `DOB ${d.dob}` : "";
  if (item.source === "web_inquiry") return d.reason || "";
  const when = [d.requested_date, d.requested_time].filter(Boolean).join(" ");
  return [when ? `Requested ${when}` : "", d.provider, d.reason].filter(Boolean).join(" · ");
}

function deptName(id) {
  if (!hasMultipleDepartments() || id === null || id === undefined) return "";
  const d = practiceDepartments().find((x) => String(x.id) === String(id));
  return d ? d.name : "";
}

function RequestRow({
  item, meta, busy, notesOpen, notesDraft, setNotesDraft, onStatus, onAssign, onToggleNotes, onSaveNotes, onCloseNotes,
  onSendIntake, onLink, onOpenView, onOpenMessages, onOpenChart,
}) {
  const key = itemKey(item);
  const type = TYPE_CHIPS[item.source] || { label: item.type_label, cls: "" };
  const chip = matchChip(item);
  const pill = chartPill(item);
  const status = STATUS_META[item.status] || STATUS_META.new;
  const loc = deptName(item.departmentid);
  const contact = [item.phone, item.email, loc].filter(Boolean).join(" · ");
  const details = requestedLine(item);
  const result = resultLine(item);
  const link = canLink(item, meta);
  const preview = (item.match && item.match.preview) || [];
  const others = item.match && item.match.count > preview.length ? item.match.count - preview.length : 0;
  const statusChoices = STATUS_ORDER.filter((x) => meta.stateReady || x !== "in_progress" || item.status === x);
  const by = item.updated_by ? `by ${item.updated_by}` : "";
  return (
    // Responsive grid (owner 10-20): below 768 px one column - details, then status / owner, then Link to
    // chart full width; from 768 px the details (and Link to chart) on the left, status / owner on the right.
    <div
      className="grid grid-cols-1 gap-3 border-b border-[#EAECF0] px-3 py-3 last:border-b-0 hover:bg-[#F9FAFB] md:grid-cols-[minmax(0,1fr)_210px] md:gap-x-4 md:gap-y-0 md:px-4"
      role="listitem"
      data-testid={`inbox-row-${key}`}
    >
      <div className="min-w-0 md:col-start-1 md:row-start-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold text-[#101828]">{item.name || "—"}</span>
          {pill && pill.patientid && (
            <button
              type="button"
              onClick={() => onOpenChart && onOpenChart(pill.patientid)}
              title="Open the chart"
              className="whitespace-nowrap rounded-full border border-[#B2DDFF] bg-[#EFF8FF] px-2 py-1 text-[11px] font-semibold text-[#175CD3] hover:bg-[#D1E9FF] md:py-[1px]"
              data-testid={`chart-${key}`}
            >
              Chart #{pill.patientid}
            </button>
          )}
          {pill && pill.none && (
            <span
              className="whitespace-nowrap rounded-full border border-[#FEDF89] bg-[#FFFAEB] px-2 py-[1px] text-[11px] font-semibold text-[#B54708]"
              data-testid={`chart-${key}`}
            >
              No chart yet
            </span>
          )}
          <span className={`whitespace-nowrap rounded-full border px-2 py-[1px] text-[11px] font-medium ${type.cls}`}>{type.label}</span>
          {chip && (
            <span className={`whitespace-nowrap rounded-full px-2 py-[1px] text-[11px] font-medium ${chip.cls}`} data-testid={`match-${key}`}>
              {chip.label}
            </span>
          )}
          <span className="whitespace-nowrap text-xs text-[#667085] md:ml-auto">
            {item.received_at ? dayjs(item.received_at).format("MM-DD-YYYY h:mm A") : ""}
          </span>
        </div>
        {contact && <div className="mt-0.5 break-words text-[13px] text-[#475467]">{contact}</div>}
        {details && <div className="mt-1 whitespace-normal break-words text-[13px] text-[#344054]">{details}</div>}
        {result && (
          <div className="mt-1 text-[13px] text-[#027A48]" data-testid={`result-${key}`}>
            {result}
            {item.routed && item.routed.routed_to && onOpenMessages && (
              <button type="button" onClick={onOpenMessages} className="ml-2 text-[#175CD3] underline hover:no-underline">
                Open messages
              </button>
            )}
          </div>
        )}
        {item.note && !notesOpen && (
          <div className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-[#B54708]" title={item.note}>
            {item.note}
          </div>
        )}
        {notesOpen && (
          <div className="mt-2 rounded-lg bg-[#F8FAFC] p-2">
            {item.note && (
              <div className="mb-2 whitespace-pre-wrap text-xs text-[#475467]" data-testid={`notes-${key}`}>
                {item.note}
              </div>
            )}
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-start">
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={2}
                maxLength={item.source === "web_inquiry" ? 500 : 4000}
                className="flex-1 rounded-md border border-[#D5D5D5] p-2 text-sm outline-none"
                placeholder="Add a note (kept with your name and the time)"
              />
              <div className="flex flex-row gap-2 sm:flex-col sm:gap-1">
                <button type="button" disabled={busy || !notesDraft.trim()} onClick={onSaveNotes} className="min-h-[44px] flex-1 rounded-md bg-[#1D2951] px-3 py-1 text-sm text-white hover:opacity-90 disabled:opacity-50 sm:min-h-0">
                  Add note
                </button>
                <button type="button" onClick={onCloseNotes} className="min-h-[44px] flex-1 rounded-md border border-[#D5D5D5] px-3 py-1 text-sm text-[#1d2951] sm:min-h-0">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex w-full min-w-0 flex-col items-stretch gap-1 md:col-start-2 md:row-span-2 md:row-start-1">
        <select
          value={item.status}
          disabled={busy}
          aria-label="Status"
          onChange={(e) => onStatus(e.target.value)}
          className={`h-11 w-full rounded-full px-2 text-sm font-medium outline-none md:h-7 md:text-xs ${status.cls}`}
        >
          {item.status === "expired" && (
            <option value="expired" disabled>
              Expired
            </option>
          )}
          {statusChoices.map((x) => (
            <option key={x} value={x}>
              {statusLabel(item.source, x)}
            </option>
          ))}
        </select>
        {meta.stateReady ? (
          <select
            value={item.assignee_is_default ? "" : item.assignee_userid ?? ""}
            disabled={busy}
            aria-label="Assigned to"
            onChange={(e) => onAssign(e.target.value === "" ? null : Number(e.target.value))}
            className="h-11 w-full rounded-md border border-[#D0D5DD] bg-white px-1 text-sm text-[#1D2951] md:h-7 md:text-xs"
          >
            <option value="">{item.assignee_is_default && item.assignee_name ? `${item.assignee_name} (default)` : "Unassigned"}</option>
            {meta.staff.map((st) => (
              <option key={st.user_id} value={st.user_id}>
                {st.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs text-[#667085]">{item.assignee_name || "Unassigned"}</span>
        )}
        {by && <div className="px-1 text-[11px] leading-tight text-[#98A2B3]" data-testid={`by-${key}`}>{by}</div>}
        <div className="flex items-center justify-end gap-2 text-[#667085]">
          {item.source === "web_new_patient" && (
            <button type="button" title="Text intake link" disabled={busy} onClick={onSendIntake} className="flex h-11 w-11 items-center justify-center hover:text-[#1D2951] md:h-auto md:w-auto">
              <SendOutlinedIcon fontSize="small" />
            </button>
          )}
          {item.source === "onboarding" && onOpenView && (
            <button type="button" title="Open in Onboarding" onClick={() => onOpenView("onboarding")} className="flex h-11 w-11 items-center justify-center hover:text-[#1D2951] md:h-auto md:w-auto">
              <OpenInNewIcon fontSize="small" />
            </button>
          )}
          {(isWeb(item) || item.source === "web_inquiry" || meta.stateReady) && (
            <button
              type="button"
              title={item.note ? "Note (has content)" : "Note"}
              disabled={busy}
              onClick={onToggleNotes}
              className={`flex h-11 w-11 items-center justify-center md:h-auto md:w-auto ${item.note ? "text-[#E17954]" : "hover:text-[#1D2951]"}`}
            >
              <StickyNote2OutlinedIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
      {link && (
        <div className="flex flex-col items-stretch gap-2 md:col-start-1 md:row-start-2 md:mt-2 md:flex-row md:flex-wrap md:items-center">
          <button
            type="button"
            disabled={busy}
            onClick={onLink}
            className="h-11 w-full rounded-md bg-[#1D2951] px-3 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-50 md:h-auto md:w-auto md:py-1.5 md:text-[13px]"
            data-testid={`link-${key}`}
          >
            Link to chart
          </button>
          <span className="break-words text-[13px] text-[#475467]" data-testid={`preview-${key}`}>
            {preview.map((p) => `${p.name || "Chart"}${p.dob ? ` · DOB ${p.dob}` : ""} (#${p.patientid})`).join("; ")}
            {others > 0 ? ` +${others} more` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

export default function RequestsQueue({ onOpenView, onOpenMessages, compact = false }) {
  const navigate = useNavigate();
  // The dashboard opens the patient (brainycarehome reads state.fromPatientId), as the fax
  // viewer's patient link does.
  const openChart = (patientid) => navigate("/landing", { state: { fromPatientId: patientid } });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({ staff: [], stateReady: false, routingReady: false, sources: [] });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [sourceFilter, setSourceFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [notesOpenKey, setNotesOpenKey] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [confirmSend, setConfirmSend] = useState(null);
  const [linkFor, setLinkFor] = useState(null); // {item, candidates, loading, pick}

  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);
  const toast = (msg, severity = "success") => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  // search: 300 ms after the last keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter, search]);

  const fetchRows = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const res = await listRequests({
          status: statusFilter,
          q: search,
          source: sourceFilter,
          page,
          pageSize: compact ? 10 : PAGE_SIZE,
        });
        setRows(Array.isArray(res?.data) ? res.data : []);
        setTotal(Number(res?.total) || 0);
        setMeta({
          staff: Array.isArray(res?.staff) ? res.staff : [],
          stateReady: res?.state_ready === true,
          routingReady: res?.routing_ready === true,
          sources: Array.isArray(res?.sources) ? res.sources : [],
        });
      } catch (e) {
        if (!silent) {
          setRows([]);
          setTotal(0);
          toast(inboxErrorMessage(e, "Could not load requests"), "error");
        }
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, search, sourceFilter, page, compact],
  );

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const changed = () => {
    eventBus.emit("inbox:changed");
    fetchRows(true);
  };

  const save = async (item, changes, okText) => {
    try {
      setBusy(true);
      await updateItem(item, changes);
      if (okText) toast(okText);
      changed();
      return true;
    } catch (e) {
      toast(inboxErrorMessage(e, "Update failed"), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async (item) => {
    if (!notesDraft.trim()) return;
    if (await save(item, { note: notesDraft }, "Note added")) setNotesOpenKey(null);
  };

  // Notes are appended (with login + time) by the server; the box adds one.
  const toggleNotes = (item) => {
    if (notesOpenKey === itemKey(item)) {
      setNotesOpenKey(null);
    } else {
      setNotesOpenKey(itemKey(item));
      setNotesDraft("");
    }
  };

  const doSendIntakeLink = async (item) => {
    try {
      setBusy(true);
      await sendIntakeLink(item);
      toast(`Intake link texted to ${item.phone}`);
      changed();
    } catch (e) {
      toast(inboxErrorMessage(e, "Send failed"), "error");
    } finally {
      setBusy(false);
      setConfirmSend(null);
    }
  };

  const openLink = async (item) => {
    setLinkFor({ item, candidates: [], loading: true, pick: null });
    try {
      const res = await itemCandidates(item);
      const cands = Array.isArray(res?.candidates) ? res.candidates : [];
      setLinkFor({ item, candidates: cands, loading: false, pick: cands.length === 1 ? cands[0].patientid : null });
    } catch (e) {
      setLinkFor(null);
      toast(inboxErrorMessage(e, "Could not load the matching charts"), "error");
    }
  };

  const doLink = async () => {
    if (!linkFor || !linkFor.pick) return;
    try {
      setBusy(true);
      await linkItem(linkFor.item, linkFor.pick);
      toast("Sent to the patient's chat");
      setLinkFor(null);
      changed();
    } catch (e) {
      toast(inboxErrorMessage(e, "Could not link the request"), "error");
    } finally {
      setBusy(false);
    }
  };

  const typeChoices = useMemo(
    () => [{ value: "", label: "All types" }, ...meta.sources.map((s) => ({ value: s, label: (TYPE_CHIPS[s] || {}).label || s }))],
    [meta.sources],
  );
  const pageSize = compact ? 10 : PAGE_SIZE;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="w-full min-w-0 rounded-[20px] bg-[#FFF] p-2 text-left md:p-4" data-testid="inbox-requests">
      {/* Toolbar */}
      {!compact && (
        <div className="mb-3 flex w-full min-w-0 flex-wrap items-center justify-between gap-3">
          <div className="flex w-full min-w-0 items-center gap-2 overflow-x-auto pb-1 md:w-auto md:flex-wrap md:overflow-visible md:pb-0">
            {FILTER_CHIPS.map((chip) => (
              <button
                key={chip.value}
                type="button"
                onClick={() => setStatusFilter(chip.value)}
                className={`min-h-[44px] shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors md:min-h-0 ${
                  statusFilter === chip.value
                    ? "border-[#1D2951] bg-[#1D2951] text-white"
                    : "border-[#D5D5D5] bg-white text-[#1d2951] hover:bg-[#F5F6FA]"
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <div className="flex w-full min-w-0 flex-wrap items-center gap-2 md:w-auto md:flex-nowrap md:gap-3">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              aria-label="Request type"
              className="h-11 w-full min-w-0 rounded-md border border-[#D0D5DD] bg-white px-2 text-[13px] text-[#1D2951] md:h-8 md:w-auto"
            >
              {typeChoices.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <div className="flex min-w-0 flex-1 items-center rounded-[15px] border border-[#D5D5D5] bg-[#F5F6FA] md:flex-none">
              <FontAwesomeIcon icon={faSearch} className="ml-3 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="h-11 w-full min-w-0 rounded-[15px] bg-[#F5F6FA] py-1 pl-2 pr-3 text-sm outline-none md:h-auto md:w-[220px]"
                placeholder="Search name, phone, reason, note"
                aria-label="Search requests"
              />
            </div>
            <button type="button" onClick={() => fetchRows()} title="Refresh" className="flex h-11 w-11 items-center justify-center text-[#667085] hover:text-[#1d2951] md:h-auto md:w-auto">
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {/* Rows (owner 10-20: no table, no sideways scroll at 1280 px) */}
      <div className="max-h-[66vh] overflow-y-auto rounded-xl border border-[#E4E7EC]" role="list" aria-label="Requests">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">No requests</div>
        ) : (
          rows.map((item) => (
            <RequestRow
              key={itemKey(item)}
              item={item}
              meta={meta}
              busy={busy}
              notesOpen={notesOpenKey === itemKey(item)}
              notesDraft={notesDraft}
              setNotesDraft={setNotesDraft}
              onStatus={(st) => save(item, { status: st }, `Marked ${statusLabel(item.source, st)}`)}
              onAssign={(uid) => save(item, { assignee_userid: uid }, "Assignee saved")}
              onToggleNotes={() => toggleNotes(item)}
              onSaveNotes={() => saveNotes(item)}
              onCloseNotes={() => setNotesOpenKey(null)}
              onSendIntake={() => setConfirmSend(item)}
              onLink={() => openLink(item)}
              onOpenView={onOpenView}
              onOpenMessages={onOpenMessages}
              onOpenChart={openChart}
            />
          ))
        )}
      </div>

      {!compact && total > pageSize && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-3 text-sm text-[#475467]">
          <span>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </span>
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((n) => Math.max(1, n - 1))} className="min-h-[44px] rounded-md border border-[#D0D5DD] px-3 py-1 disabled:opacity-50 md:min-h-0">
            Previous
          </button>
          <button type="button" disabled={page >= pages || loading} onClick={() => setPage((n) => n + 1)} className="min-h-[44px] rounded-md border border-[#D0D5DD] px-3 py-1 disabled:opacity-50 md:min-h-0">
            Next
          </button>
        </div>
      )}

      {/* Intake-link confirmation (as the Web Requests tab) */}
      <Dialog open={!!confirmSend} onClose={() => setConfirmSend(null)}>
        <DialogTitle>Text intake link?</DialogTitle>
        <DialogContent>
          <div className="text-sm text-[#4B5563]">
            Send the <b>{deptName(confirmSend?.departmentid) || practiceDepartmentName(defaultDepartmentId(), "Odessa")}</b> new-patient intake link by SMS to <b>{confirmSend?.name}</b> at{" "}
            <b>{confirmSend?.phone}</b>?
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={() => doSendIntakeLink(confirmSend)} style={{ backgroundColor: "#1D2951" }}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link to chart: the charts with this phone (and last name) */}
      <Dialog open={!!linkFor} onClose={() => setLinkFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link to chart</DialogTitle>
        <DialogContent>
          {linkFor?.loading ? (
            <div className="py-6 text-center text-sm text-[#667085]">Loading...</div>
          ) : linkFor && linkFor.candidates.length === 0 ? (
            <div className="text-sm text-[#4B5563]">No chart has this phone number any more.</div>
          ) : (
            <div className="flex flex-col gap-2 text-sm text-[#344054]" role="radiogroup" aria-label="Charts">
              <div className="mb-1 text-[#4B5563]">
                Pick the chart this request belongs to. It is posted into that patient&apos;s chat and marked done here.
              </div>
              {(linkFor?.candidates || []).map((c) => (
                <label key={c.patientid} className="flex cursor-pointer items-center gap-2 rounded-md border border-[#E4E7EC] px-3 py-2 hover:bg-[#F8FAFC]">
                  <input
                    type="radio"
                    name="inbox-link-chart"
                    checked={linkFor.pick === c.patientid}
                    onChange={() => setLinkFor((s) => ({ ...s, pick: c.patientid }))}
                  />
                  <span className="font-medium">
                    {c.firstname} {c.lastname}
                  </span>
                  <span className="text-[#667085]">
                    {c.dob ? `DOB ${c.dob} · ` : ""}Chart {c.patientid}
                  </span>
                </label>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !linkFor?.pick} onClick={doLink} style={{ backgroundColor: "#1D2951", color: "#fff" }}>
            Send to chat
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={busy} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <ToastMessage open={openToast} handleClose={() => setOpenToast(false)} message={toastMessage} severity={toastSeverity} />
    </div>
  );
}
